/**
 * useRadar — React Query hook for the book-wide Change Radar (TASK-477).
 * Mirrors useDashboardCompliance. Live refresh is driven by the caller refetching
 * when the alert store changes (a critical change arrives as a new_alert over the
 * existing alert WebSocket) — no separate radar transport.
 */

import { useQuery } from '@tanstack/react-query';
import { radarService, RadarResponse } from '../services/radarService';

/**
 * TASK-818 (DEC-1): the page limit, raised from 20 and named so it stops being a magic
 * number in a call site.
 *
 * 20 cut a third of the book's DNA exposure out of the list while the banner one line above
 * reported the honest book-wide total — the surface contradicted itself in a single glance.
 * Live on 2026-08-25: 9 non-DNA rows + 11 DNA rows = exactly 20, with 11 more DNA
 * instruments and CHF 1'469'191 invisible and no way to learn that.
 *
 * BOUNDED, not removed. `limit` is also the query's bound, and an unbounded list on the page
 * an advisor scans first is a different decision nobody asked for (TASK-753 already records a
 * red scroll budget on this dashboard). 100 covers the observed book with headroom; whatever
 * a larger book still cuts is covered by the truncation notice, which is the honesty half and
 * must not be removed because the cap went up.
 */
export const RADAR_PAGE_LIMIT = 100;

export function useRadar(limit = RADAR_PAGE_LIMIT) {
  return useQuery<RadarResponse>({
    queryKey: ['radar', limit],
    queryFn: () => radarService.get(limit),
    staleTime: 60_000,
  });
}
